import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ChevronRightIcon, SaveIcon, SendIcon, UserIcon, CalendarIcon, MapPinIcon, PhoneIcon, MailIcon, FileTextIcon, CheckCircleIcon, AlertCircleIcon, LoaderIcon, InfoIcon, HelpCircleIcon } from 'lucide-react';
interface FormField {
  id: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'date' | 'select' | 'textarea' | 'number' | 'checkbox' | 'file' | 'radio';
  placeholder?: string;
  required: boolean;
  options?: string[];
  value: string | boolean | File | null;
  helpText?: string;
  validationPattern?: string;
  validationMessage?: string;
  dependsOn?: {
    field: string;
    value: string | boolean;
  };
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  groupName?: string;
}
interface FormSection {
  id: string;
  title: string;
  description?: string;
  fields: FormField[];
}
// Mock citizen data
const mockCitizenData = {
  '1': {
    id: '1',
    firstName: 'Abebe',
    lastName: 'Kebede',
    name: 'Abebe Kebede',
    idNumber: 'ETH-1234567',
    dateOfBirth: '1985-05-12',
    address: '123 Bole Road, Addis Ababa',
    phoneNumber: '+251 91 234 5678',
    email: 'abebe.kebede@example.com',
    gender: 'Male',
    nationality: 'Ethiopian'
  },
  '2': {
    id: '2',
    firstName: 'Tigist',
    lastName: 'Haile',
    name: 'Tigist Haile',
    idNumber: 'ETH-7654321',
    dateOfBirth: '1990-10-25',
    address: '456 Churchill Avenue, Addis Ababa',
    phoneNumber: '+251 92 345 6789',
    email: 'tigist.haile@example.com',
    gender: 'Female',
    nationality: 'Ethiopian'
  },
  '3': {
    id: '3',
    firstName: 'Dawit',
    lastName: 'Mulugeta',
    name: 'Dawit Mulugeta',
    idNumber: 'ETH-8765432',
    dateOfBirth: '1978-03-18',
    address: '789 Mekelle Road, Mekelle',
    phoneNumber: '+251 93 456 7890',
    email: 'dawit.mulugeta@example.com',
    gender: 'Male',
    nationality: 'Ethiopian'
  },
  '4': {
    id: '4',
    firstName: 'Meseret',
    lastName: 'Tadesse',
    name: 'Meseret Tadesse',
    idNumber: 'ETH-2345678',
    dateOfBirth: '1992-07-30',
    address: '321 Hawassa Street, Hawassa',
    phoneNumber: '+251 94 567 8901',
    email: 'meseret.tadesse@example.com',
    gender: 'Female',
    nationality: 'Ethiopian'
  },
  '5': {
    id: '5',
    firstName: 'Yonas',
    lastName: 'Tefera',
    name: 'Yonas Tefera',
    idNumber: 'ETH-3456789',
    dateOfBirth: '1982-12-05',
    address: '654 Gondar Road, Gondar',
    phoneNumber: '+251 95 678 9012',
    email: 'yonas.tefera@example.com',
    gender: 'Male',
    nationality: 'Ethiopian'
  },
  'new-1234567890': {
    id: 'new-1234567890',
    firstName: 'New',
    lastName: 'Citizen',
    name: 'New Citizen',
    idNumber: 'ETH-9876543',
    dateOfBirth: '1995-08-15',
    address: '',
    phoneNumber: '',
    email: '',
    gender: 'Female',
    nationality: 'Ethiopian'
  }
};
// Mock service data
const mockServiceData = {
  lt1: {
    id: 'lt1',
    title: 'New Land Title Registration',
    description: 'Apply for first-time registration of land title',
    category: 'Land Title Services',
    estimatedTime: '15-20 business days',
    fee: '$250.00',
    sections: [{
      id: 'propertyInfo',
      title: 'Property Information',
      description: 'Details about the property being registered',
      fields: [{
        id: 'propertyAddress',
        label: 'Property Address',
        type: 'text',
        placeholder: 'Enter full property address',
        required: true,
        value: '',
        helpText: 'Please provide the complete address including street, city and postal code'
      }, {
        id: 'propertySize',
        label: 'Property Size (in square meters)',
        type: 'number',
        placeholder: 'Enter property size',
        required: true,
        value: '',
        minValue: 1,
        maxValue: 1000000,
        helpText: 'Must be a positive number'
      }, {
        id: 'propertyType',
        label: 'Property Type',
        type: 'select',
        required: true,
        options: ['Residential', 'Commercial', 'Agricultural', 'Industrial', 'Mixed Use'],
        value: ''
      }, {
        id: 'zoning',
        label: 'Zoning Classification',
        type: 'select',
        required: true,
        options: ['Residential (R1)', 'Residential (R2)', 'Commercial (C1)', 'Commercial (C2)', 'Industrial (I1)', 'Agricultural (A1)', 'Mixed Use (MU)'],
        value: '',
        helpText: 'Select the official zoning classification for this property'
      }]
    }, {
      id: 'acquisitionInfo',
      title: 'Acquisition Information',
      description: 'Details about how the property was acquired',
      fields: [{
        id: 'previousOwner',
        label: 'Previous Owner (if applicable)',
        type: 'text',
        placeholder: 'Enter previous owner name',
        required: false,
        value: ''
      }, {
        id: 'acquisitionDate',
        label: 'Date of Acquisition',
        type: 'date',
        required: true,
        value: ''
      }, {
        id: 'acquisitionMethod',
        label: 'Method of Acquisition',
        type: 'select',
        required: true,
        options: ['Purchase', 'Inheritance', 'Gift', 'Government Allocation', 'Other'],
        value: ''
      }, {
        id: 'acquisitionOther',
        label: 'Specify Other Acquisition Method',
        type: 'text',
        placeholder: 'Please specify the acquisition method',
        required: false,
        value: '',
        dependsOn: {
          field: 'acquisitionMethod',
          value: 'Other'
        }
      }]
    }, {
      id: 'documents',
      title: 'Supporting Documents',
      description: 'Upload required documentation to support your application',
      fields: [{
        id: 'identificationDoc',
        label: 'Identification Document',
        type: 'file',
        required: true,
        value: null,
        helpText: 'Upload a scanned copy of your national ID or passport'
      }, {
        id: 'proofOfOwnership',
        label: 'Proof of Ownership',
        type: 'file',
        required: true,
        value: null,
        helpText: 'Upload deed, bill of sale, or other ownership documentation'
      }, {
        id: 'surveyCertificate',
        label: 'Survey Certificate',
        type: 'file',
        required: true,
        value: null,
        helpText: 'Upload official property survey certificate'
      }, {
        id: 'taxClearance',
        label: 'Tax Clearance Certificate',
        type: 'file',
        required: true,
        value: null,
        helpText: 'Upload proof that all property taxes are paid'
      }, {
        id: 'additionalDocs',
        label: 'Additional Supporting Documents',
        type: 'file',
        required: false,
        value: null,
        helpText: 'Upload any additional relevant documents'
      }]
    }, {
      id: 'additionalInfo',
      title: 'Additional Information',
      description: 'Provide any additional details relevant to your application',
      fields: [{
        id: 'propertyUse',
        label: 'Intended Use of Property',
        type: 'textarea',
        placeholder: 'Describe how you plan to use the property',
        required: true,
        value: '',
        maxLength: 500
      }, {
        id: 'improvements',
        label: 'Existing Improvements',
        type: 'textarea',
        placeholder: 'Describe any buildings or improvements on the property',
        required: true,
        value: '',
        maxLength: 500
      }, {
        id: 'additionalInfo',
        label: 'Additional Information',
        type: 'textarea',
        placeholder: 'Enter any additional relevant information',
        required: false,
        value: '',
        maxLength: 1000
      }]
    }, {
      id: 'declaration',
      title: 'Declaration',
      description: 'Please read and confirm the following statements',
      fields: [{
        id: 'declarationTruth',
        label: 'I declare that all information provided in this application is true and accurate to the best of my knowledge',
        type: 'checkbox',
        required: true,
        value: false
      }, {
        id: 'declarationDocuments',
        label: 'I confirm that all documents submitted are authentic and valid',
        type: 'checkbox',
        required: true,
        value: false
      }, {
        id: 'declarationInspection',
        label: 'I understand that the property may be subject to inspection as part of the registration process',
        type: 'checkbox',
        required: true,
        value: false
      }, {
        id: 'declarationFees',
        label: 'I agree to pay all applicable fees associated with this application',
        type: 'checkbox',
        required: true,
        value: false
      }]
    }]
  },
  bd1: {
    id: 'bd1',
    title: 'Birth Certificate Request',
    description: 'Apply for new or replacement birth certificates',
    category: 'Birth & Death Records',
    estimatedTime: '3-5 business days',
    fee: '$25.00',
    sections: [{
      id: 'childInfo',
      title: 'Child Information',
      description: 'Details about the child whose birth certificate is being requested',
      fields: [{
        id: 'childName',
        label: 'Full Name of Child',
        type: 'text',
        placeholder: 'Enter full name',
        required: true,
        value: ''
      }, {
        id: 'dateOfBirth',
        label: 'Date of Birth',
        type: 'date',
        required: true,
        value: ''
      }, {
        id: 'placeOfBirth',
        label: 'Place of Birth',
        type: 'text',
        placeholder: 'Enter city/town and facility',
        required: true,
        value: '',
        helpText: 'Specify the hospital, health center, or location where birth occurred'
      }, {
        id: 'gender',
        label: 'Gender',
        type: 'radio',
        required: true,
        options: ['Male', 'Female', 'Other'],
        value: '',
        groupName: 'gender'
      }]
    }, {
      id: 'parentInfo',
      title: 'Parent Information',
      description: "Details about the child's parents",
      fields: [{
        id: 'fatherName',
        label: "Father's Full Name",
        type: 'text',
        placeholder: "Enter father's full name",
        required: true,
        value: ''
      }, {
        id: 'fatherNationality',
        label: "Father's Nationality",
        type: 'text',
        placeholder: "Enter father's nationality",
        required: true,
        value: ''
      }, {
        id: 'motherName',
        label: "Mother's Full Name",
        type: 'text',
        placeholder: "Enter mother's full name",
        required: true,
        value: ''
      }, {
        id: 'motherNationality',
        label: "Mother's Nationality",
        type: 'text',
        placeholder: "Enter mother's nationality",
        required: true,
        value: ''
      }]
    }, {
      id: 'certificateInfo',
      title: 'Certificate Information',
      description: 'Details about the certificate being requested',
      fields: [{
        id: 'certificateType',
        label: 'Certificate Type',
        type: 'select',
        required: true,
        options: ['New Certificate', 'Replacement', 'Certified Copy'],
        value: ''
      }, {
        id: 'reason',
        label: 'Reason for Request (if replacement)',
        type: 'textarea',
        placeholder: 'Explain why you need a replacement',
        required: false,
        value: '',
        dependsOn: {
          field: 'certificateType',
          value: 'Replacement'
        }
      }, {
        id: 'numberOfCopies',
        label: 'Number of Copies',
        type: 'number',
        placeholder: 'Enter number of copies needed',
        required: true,
        value: '1',
        minValue: 1,
        maxValue: 10
      }]
    }, {
      id: 'documents',
      title: 'Supporting Documents',
      description: 'Upload required documentation to support your application',
      fields: [{
        id: 'identificationDoc',
        label: 'Requestor Identification',
        type: 'file',
        required: true,
        value: null,
        helpText: 'Upload a scanned copy of your national ID or passport'
      }, {
        id: 'relationshipProof',
        label: 'Proof of Relationship',
        type: 'file',
        required: true,
        value: null,
        helpText: 'Upload document proving your relationship to the child (e.g., existing birth certificate, family book)'
      }, {
        id: 'additionalDocs',
        label: 'Additional Supporting Documents',
        type: 'file',
        required: false,
        value: null,
        helpText: 'Upload any additional relevant documents'
      }]
    }, {
      id: 'declaration',
      title: 'Declaration',
      description: 'Please read and confirm the following statements',
      fields: [{
        id: 'declarationTruth',
        label: 'I declare that all information provided in this application is true and accurate to the best of my knowledge',
        type: 'checkbox',
        required: true,
        value: false
      }, {
        id: 'declarationDocuments',
        label: 'I confirm that all documents submitted are authentic and valid',
        type: 'checkbox',
        required: true,
        value: false
      }, {
        id: 'declarationFees',
        label: 'I agree to pay all applicable fees associated with this application',
        type: 'checkbox',
        required: true,
        value: false
      }]
    }]
  }
};
export const FormFillingPage = () => {
  const {
    serviceId,
    citizenId
  } = useParams<{
    serviceId: string;
    citizenId: string;
  }>();
  const navigate = useNavigate();
  const citizen = citizenId ? mockCitizenData[citizenId as keyof typeof mockCitizenData] : null;
  const service = serviceId ? mockServiceData[serviceId as keyof typeof mockServiceData] : null;
  const [formSections, setFormSections] = useState<FormSection[]>(service?.sections || []);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submissionId, setSubmissionId] = useState('');
  useEffect(() => {
    // Pre-fill some fields if possible
    if (citizen && formSections.length > 0) {
      const updatedSections = [...formSections];
      // Example: Pre-fill applicant information if available
      if (serviceId === 'bd1') {
        // For birth certificate, we might pre-fill parent information
        const parentInfoSection = updatedSections.find(s => s.id === 'parentInfo');
        if (parentInfoSection) {
          const fatherNameField = parentInfoSection.fields.find(f => f.id === 'fatherName');
          if (fatherNameField && citizen.gender === 'Male') {
            fatherNameField.value = citizen.name;
          }
          const motherNameField = parentInfoSection.fields.find(f => f.id === 'motherName');
          if (motherNameField && citizen.gender === 'Female') {
            motherNameField.value = citizen.name;
          }
        }
      }
      setFormSections(updatedSections);
    }
  }, [citizen, formSections.length, serviceId]);
  if (!citizen || !service) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-3215">
        <div className="bg-white p-8 rounded-lg shadow-md" data-id="element-3216">
          <h1 className="text-xl font-semibold text-red-600 mb-4" data-id="element-3217">Error</h1>
          <p className="text-gray-700 mb-4" data-id="element-3218">
            Invalid service or citizen information. Please go back and try
            again.
          </p>
          <button onClick={() => navigate(-1)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-3219">
            Go Back
          </button>
        </div>
      </div>;
  }
  const handleInputChange = (sectionIndex: number, fieldId: string, value: string | boolean | File | null) => {
    const updatedSections = [...formSections];
    const fieldIndex = updatedSections[sectionIndex].fields.findIndex(f => f.id === fieldId);
    if (fieldIndex !== -1) {
      updatedSections[sectionIndex].fields[fieldIndex].value = value;
      setFormSections(updatedSections);
      // Clear error when field is edited
      if (formErrors[fieldId]) {
        setFormErrors({
          ...formErrors,
          [fieldId]: ''
        });
      }
    }
  };
  const validateSection = (sectionIndex: number): boolean => {
    const section = formSections[sectionIndex];
    const errors: Record<string, string> = {};
    let isValid = true;
    section.fields.forEach(field => {
      // Skip validation for fields that depend on other fields if the condition isn't met
      if (field.dependsOn) {
        const dependentField = formSections.flatMap(s => s.fields).find(f => f.id === field.dependsOn?.field);
        if (dependentField && dependentField.value !== field.dependsOn.value) {
          return;
        }
      }
      if (field.required) {
        if (field.type === 'checkbox' && field.value === false) {
          errors[field.id] = 'You must accept this declaration';
          isValid = false;
        } else if (field.type === 'file' && !field.value) {
          errors[field.id] = 'Please upload a file';
          isValid = false;
        } else if (field.type !== 'checkbox' && field.type !== 'file' && (!field.value || field.value === '')) {
          errors[field.id] = 'This field is required';
          isValid = false;
        }
      }
      // Additional validation based on field type
      if (field.value) {
        if (field.type === 'email' && typeof field.value === 'string') {
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailPattern.test(field.value)) {
            errors[field.id] = 'Please enter a valid email address';
            isValid = false;
          }
        } else if (field.type === 'tel' && typeof field.value === 'string') {
          const phonePattern = /^\+?[0-9\s\-()]{8,20}$/;
          if (!phonePattern.test(field.value)) {
            errors[field.id] = 'Please enter a valid phone number';
            isValid = false;
          }
        } else if (field.type === 'number' && typeof field.value === 'string') {
          const numValue = parseFloat(field.value);
          if (isNaN(numValue)) {
            errors[field.id] = 'Please enter a valid number';
            isValid = false;
          } else {
            if (field.minValue !== undefined && numValue < field.minValue) {
              errors[field.id] = `Value must be at least ${field.minValue}`;
              isValid = false;
            }
            if (field.maxValue !== undefined && numValue > field.maxValue) {
              errors[field.id] = `Value must not exceed ${field.maxValue}`;
              isValid = false;
            }
          }
        } else if (field.maxLength && typeof field.value === 'string' && field.value.length > field.maxLength) {
          errors[field.id] = `Text must not exceed ${field.maxLength} characters`;
          isValid = false;
        }
      }
      // Custom validation pattern
      if (field.validationPattern && field.value && typeof field.value === 'string' && !new RegExp(field.validationPattern).test(field.value)) {
        errors[field.id] = field.validationMessage || 'Invalid format';
        isValid = false;
      }
    });
    setFormErrors({
      ...formErrors,
      ...errors
    });
    return isValid;
  };
  const validateForm = (): boolean => {
    let isValid = true;
    for (let i = 0; i < formSections.length; i++) {
      if (!validateSection(i)) {
        isValid = false;
      }
    }
    return isValid;
  };
  const handleSectionChange = (newIndex: number) => {
    // Validate current section before moving
    if (newIndex > activeSection) {
      if (!validateSection(activeSection)) {
        return;
      }
    }
    setActiveSection(newIndex);
    // Scroll to top of form
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      // Find the first section with errors
      let sectionWithError = 0;
      for (let i = 0; i < formSections.length; i++) {
        const hasError = formSections[i].fields.some(field => formErrors[field.id]);
        if (hasError) {
          sectionWithError = i;
          break;
        }
      }
      setActiveSection(sectionWithError);
      // Scroll to the first error
      const firstErrorId = Object.keys(formErrors)[0];
      const element = document.getElementById(firstErrorId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }
    setIsSubmitting(true);
    // Simulate API submission
    setTimeout(() => {
      // Generate a mock submission ID
      const mockSubmissionId = `REQ-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
      setSubmissionId(mockSubmissionId);
      setIsSubmitting(false);
      setShowSuccessModal(true);
    }, 2000);
  };
  const handleSaveDraft = () => {
    setIsSaving(true);
    // Simulate saving draft
    setTimeout(() => {
      setIsSaving(false);
      alert('Your application has been saved as a draft. You can continue later.');
    }, 1500);
  };
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };
  const handleViewSubmission = () => {
    setShowSuccessModal(false);
    // In a real app, this would navigate to a submission tracking page
    navigate('/');
  };
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-3220">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3221">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3222">
          <button onClick={handleBack} className="hover:text-gray-700 flex items-center" data-id="element-3223">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-3224" />
            Back to Citizen Selection
          </button>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3225" />
          <span className="text-gray-900" data-id="element-3226">Form Filling</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3227">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3228">
            {service.title}
          </h1>
          <p className="text-gray-600" data-id="element-3229">{service.description}</p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm" data-id="element-3230">
            <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full flex items-center" data-id="element-3231">
              <span className="w-4 h-4 text-blue-500 mr-1.5" data-id="element-3232">⏱</span>
              <span data-id="element-3233">Processing time: {service.estimatedTime}</span>
            </div>
            <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full flex items-center" data-id="element-3234">
              <span className="w-4 h-4 text-green-500 mr-1.5" data-id="element-3235">💰</span>
              <span data-id="element-3236">Fee: {service.fee}</span>
            </div>
          </div>
        </div>
        {/* Citizen Information */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6" data-id="element-3237">
          <div className="flex items-start space-x-4" data-id="element-3238">
            <div className="flex-shrink-0" data-id="element-3239">
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-3240">
                <UserIcon className="h-6 w-6 text-gray-500" data-id="element-3241" />
              </div>
            </div>
            <div className="flex-1" data-id="element-3242">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-3243">
                Applicant Information
              </h2>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-id="element-3244">
                <div className="flex items-center" data-id="element-3245">
                  <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-3246" />
                  <span className="font-medium text-gray-700" data-id="element-3247">Name:</span>{' '}
                  <span className="ml-1" data-id="element-3248">{citizen.name}</span>
                </div>
                <div className="flex items-center" data-id="element-3249">
                  <FileTextIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-3250" />
                  <span className="font-medium text-gray-700" data-id="element-3251">
                    ID Number:
                  </span>{' '}
                  <span className="ml-1" data-id="element-3252">{citizen.idNumber}</span>
                </div>
                <div className="flex items-center" data-id="element-3253">
                  <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-3254" />
                  <span className="font-medium text-gray-700" data-id="element-3255">
                    Date of Birth:
                  </span>{' '}
                  <span className="ml-1" data-id="element-3256">
                    {formatDate(citizen.dateOfBirth)}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-3257">
                  <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-3258" />
                  <span className="font-medium text-gray-700" data-id="element-3259">Phone:</span>{' '}
                  <span className="ml-1" data-id="element-3260">
                    {citizen.phoneNumber || 'Not provided'}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-3261">
                  <MailIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-3262" />
                  <span className="font-medium text-gray-700" data-id="element-3263">Email:</span>{' '}
                  <span className="ml-1" data-id="element-3264">
                    {citizen.email || 'Not provided'}
                  </span>
                </div>
                <div className="md:col-span-2 flex items-start" data-id="element-3265">
                  <MapPinIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" data-id="element-3266" />
                  <span className="font-medium text-gray-700" data-id="element-3267">
                    Address:
                  </span>{' '}
                  <span className="ml-1" data-id="element-3268">
                    {citizen.address || 'Not provided'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Form */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden" data-id="element-3269">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-3270">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-3271">
              Application Form
            </h2>
            <p className="text-sm text-gray-500" data-id="element-3272">
              Please complete all required fields marked with an asterisk (*)
            </p>
          </div>
          <div className="p-6" data-id="element-3273">
            {/* Section Navigation */}
            <div className="mb-8" data-id="element-3274">
              <div className="flex flex-nowrap overflow-x-auto pb-2 space-x-1 md:space-x-2" data-id="element-3275">
                {formSections.map((section, index) => <button key={section.id} className={`flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium ${index === activeSection ? 'bg-blue-600 text-white' : index < activeSection ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-600'}`} onClick={() => handleSectionChange(index)} data-id="element-3276">
                    {index < activeSection && <CheckCircleIcon className="inline-block h-4 w-4 mr-1" data-id="element-3277" />}
                    {index + 1}. {section.title}
                  </button>)}
              </div>
            </div>
            {/* Active Section */}
            <form onSubmit={handleSubmit} data-id="element-3278">
              <div className="space-y-8" data-id="element-3279">
                {formSections.map((section, sectionIndex) => <div key={section.id} className={sectionIndex === activeSection ? 'block' : 'hidden'} data-id="element-3280">
                    <div className="mb-6" data-id="element-3281">
                      <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="element-3282">
                        {section.title}
                      </h3>
                      {section.description && <p className="text-sm text-gray-600" data-id="element-3283">
                          {section.description}
                        </p>}
                    </div>
                    <div className="space-y-6" data-id="element-3284">
                      {section.fields.map(field => {
                    // Check if field should be shown based on dependencies
                    if (field.dependsOn) {
                      const dependentField = formSections.flatMap(s => s.fields).find(f => f.id === field.dependsOn?.field);
                      if (dependentField && dependentField.value !== field.dependsOn.value) {
                        return null;
                      }
                    }
                    return <div key={field.id} id={field.id} className={field.type === 'checkbox' ? 'flex items-start' : ''} data-id="element-3285">
                            {field.type === 'checkbox' ? <>
                                <div className="flex items-center h-5 mt-1" data-id="element-3286">
                                  <input id={field.id} type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked={field.value as boolean} onChange={e => handleInputChange(sectionIndex, field.id, e.target.checked)} data-id="element-3287" />
                                </div>
                                <div className="ml-3 text-sm" data-id="element-3288">
                                  <label htmlFor={field.id} className="font-medium text-gray-700" data-id="element-3289">
                                    {field.label}{' '}
                                    {field.required && <span className="text-red-500" data-id="element-3290">*</span>}
                                  </label>
                                  {field.helpText && <p className="text-gray-500 mt-1" data-id="element-3291">
                                      {field.helpText}
                                    </p>}
                                  {formErrors[field.id] && <p className="mt-1 text-sm text-red-600" data-id="element-3292">
                                      {formErrors[field.id]}
                                    </p>}
                                </div>
                              </> : field.type === 'radio' ? <div data-id="element-3293">
                                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3294">
                                  {field.label}{' '}
                                  {field.required && <span className="text-red-500" data-id="element-3295">*</span>}
                                </label>
                                <div className="flex flex-wrap gap-4" data-id="element-3296">
                                  {field.options?.map(option => <div key={option} className="flex items-center" data-id="element-3297">
                                      <input id={`${field.id}-${option}`} name={field.groupName || field.id} type="radio" value={option} checked={field.value === option} onChange={e => handleInputChange(sectionIndex, field.id, e.target.value)} className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-3298" />
                                      <label htmlFor={`${field.id}-${option}`} className="ml-2 block text-sm text-gray-700" data-id="element-3299">
                                        {option}
                                      </label>
                                    </div>)}
                                </div>
                                {field.helpText && <p className="text-gray-500 text-sm mt-1" data-id="element-3300">
                                    {field.helpText}
                                  </p>}
                                {formErrors[field.id] && <p className="mt-1 text-sm text-red-600" data-id="element-3301">
                                    {formErrors[field.id]}
                                  </p>}
                              </div> : field.type === 'file' ? <div data-id="element-3302">
                                <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3303">
                                  {field.label}{' '}
                                  {field.required && <span className="text-red-500" data-id="element-3304">*</span>}
                                </label>
                                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-3305">
                                  <div className="space-y-1 text-center" data-id="element-3306">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true" data-id="element-3307">
                                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4h-12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" data-id="element-3308" />
                                    </svg>
                                    <div className="flex text-sm text-gray-600" data-id="element-3309">
                                      <label htmlFor={field.id} className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500" data-id="element-3310">
                                        <span data-id="element-3311">Upload a file</span>
                                        <input id={field.id} name={field.id} type="file" className="sr-only" onChange={e => handleInputChange(sectionIndex, field.id, e.target.files?.[0] || null)} data-id="element-3312" />
                                      </label>
                                      <p className="pl-1" data-id="element-3313">or drag and drop</p>
                                    </div>
                                    <p className="text-xs text-gray-500" data-id="element-3314">
                                      PNG, JPG, PDF up to 10MB
                                    </p>
                                    {field.value && <p className="text-sm text-green-600" data-id="element-3315">
                                        File selected:{' '}
                                        {(field.value as File).name}
                                      </p>}
                                  </div>
                                </div>
                                {field.helpText && <p className="text-gray-500 text-sm mt-1" data-id="element-3316">
                                    {field.helpText}
                                  </p>}
                                {formErrors[field.id] && <p className="mt-1 text-sm text-red-600" data-id="element-3317">
                                    {formErrors[field.id]}
                                  </p>}
                              </div> : <div data-id="element-3318">
                                <div className="flex justify-between" data-id="element-3319">
                                  <label htmlFor={field.id} className="block text-sm font-medium text-gray-700" data-id="element-3320">
                                    {field.label}{' '}
                                    {field.required && <span className="text-red-500" data-id="element-3321">*</span>}
                                  </label>
                                  {field.helpText && <div className="group relative" data-id="element-3322">
                                      <HelpCircleIcon className="h-4 w-4 text-gray-400 cursor-help" data-id="element-3323" />
                                      <div className="absolute right-0 z-10 w-64 p-2 text-xs text-gray-600 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-200 invisible group-hover:visible" data-id="element-3324">
                                        {field.helpText}
                                      </div>
                                    </div>}
                                </div>
                                <div className="mt-1" data-id="element-3325">
                                  {field.type === 'select' ? <select id={field.id} className={`block w-full px-3 py-2 border ${formErrors[field.id] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-md shadow-sm focus:outline-none sm:text-sm`} value={field.value as string} onChange={e => handleInputChange(sectionIndex, field.id, e.target.value)} data-id="element-3326">
                                      <option value="" data-id="element-3327">Select an option</option>
                                      {field.options?.map(option => <option key={option} value={option} data-id="element-3328">
                                          {option}
                                        </option>)}
                                    </select> : field.type === 'textarea' ? <textarea id={field.id} rows={3} className={`block w-full px-3 py-2 border ${formErrors[field.id] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-md shadow-sm focus:outline-none sm:text-sm`} placeholder={field.placeholder} value={field.value as string} onChange={e => handleInputChange(sectionIndex, field.id, e.target.value)} maxLength={field.maxLength} data-id="element-3329" /> : <input id={field.id} type={field.type} className={`block w-full px-3 py-2 border ${formErrors[field.id] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-md shadow-sm focus:outline-none sm:text-sm`} placeholder={field.placeholder} value={field.value as string} onChange={e => handleInputChange(sectionIndex, field.id, e.target.value)} min={field.minValue} max={field.maxValue} data-id="element-3330" />}
                                </div>
                                {field.maxLength && field.type === 'textarea' && typeof field.value === 'string' && <div className="mt-1 text-xs text-gray-500 text-right" data-id="element-3331">
                                      {field.value.length}/{field.maxLength}{' '}
                                      characters
                                    </div>}
                                {formErrors[field.id] && <p className="mt-1 text-sm text-red-600" data-id="element-3332">
                                    {formErrors[field.id]}
                                  </p>}
                              </div>}
                          </div>;
                  })}
                    </div>
                    {/* Navigation buttons */}
                    <div className="mt-8 flex justify-between" data-id="element-3333">
                      <button type="button" onClick={() => handleSectionChange(Math.max(0, activeSection - 1))} className={`px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${activeSection === 0 ? 'invisible' : ''}`} data-id="element-3334">
                        Previous
                      </button>
                      {activeSection < formSections.length - 1 ? <button type="button" onClick={() => handleSectionChange(activeSection + 1)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-3335">
                          Next
                        </button> : null}
                    </div>
                  </div>)}
              </div>
              {/* Form Actions */}
              {activeSection === formSections.length - 1 && <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-3336">
                  <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4" data-id="element-3337">
                    <button type="button" onClick={handleBack} className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-3338">
                      Cancel
                    </button>
                    <button type="button" onClick={handleSaveDraft} className="px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 flex items-center justify-center" disabled={isSaving} data-id="element-3339">
                      {isSaving ? <>
                          <LoaderIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-3340" />
                          Saving...
                        </> : <>
                          <SaveIcon className="h-4 w-4 mr-2" data-id="element-3341" />
                          Save Draft
                        </>}
                    </button>
                    <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center" disabled={isSubmitting} data-id="element-3342">
                      {isSubmitting ? <>
                          <LoaderIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-3343" />
                          Submitting...
                        </> : <>
                          <SendIcon className="h-4 w-4 mr-2" data-id="element-3344" />
                          Submit Application
                        </>}
                    </button>
                  </div>
                </div>}
            </form>
          </div>
        </div>
      </div>
      {/* Success Modal */}
      {showSuccessModal && <div className="fixed inset-0 z-10 overflow-y-auto" data-id="element-3345">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-3346">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-3347">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-3348"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-3349">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-3350">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-3351">
                <div className="sm:flex sm:items-start" data-id="element-3352">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-3353">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-3354" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-3355">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3356">
                      Application Submitted Successfully
                    </h3>
                    <div className="mt-2" data-id="element-3357">
                      <p className="text-sm text-gray-500" data-id="element-3358">
                        Your application has been submitted successfully. Your
                        reference number is:
                      </p>
                      <p className="text-lg font-bold text-gray-900 my-2" data-id="element-3359">
                        {submissionId}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-3360">
                        Please save this reference number for future inquiries.
                        You will also receive a confirmation email with these
                        details.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-3361">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={handleViewSubmission} data-id="element-3362">
                  View My Applications
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => navigate('/')} data-id="element-3363">
                  Return to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};